import {
  ADMIN_AUTH_ERROR,
  type AdminAuthErrorCode,
  type ApiErrorBody,
  type ApiErrorEnvelope,
} from '@/contracts';

import { adminSession } from './session';

/**
 * The console's only way out to the network.
 *
 * Where the Next.js version had two clients — one for the server, one for the
 * browser — there is now one, because there is now one runtime. What it gained
 * instead is the two things a server did for free: attaching the token, and
 * renewing it before anybody notices.
 *
 * Failures come back as a result object rather than an exception, unchanged from
 * before and for the same reason: every caller has a real answer for "the API is
 * down" — the login form shows a message, the shell renders zeroed badges — and
 * none of them is served by a stack trace. The query layer is the exception, by
 * choice: a `queryFn` passes the result through `unwrap` (`lib/unwrap.ts`), which
 * throws, because TanStack's retry and error state only work on a thrown value.
 */

/** Empty in production: the console and the API are separate origins named by a domain. */
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? '').replace(/\/+$/, '');

/**
 * Marzouki-Backend mounts everything under `/api/v1` (`setGlobalPrefix`), and
 * the probes and public pages at the root outside it. A constant rather than
 * part of each path, so a version bump is one edit and cannot be applied to
 * three routes out of six.
 */
export const API_PREFIX = '/api/v1';

/**
 * Proves a cookie-authenticated refresh was not forged.
 *
 * The refresh cookie is `SameSite=None` because the two origins differ, so a
 * third-party page can make the browser send it — and refreshing rotates, so a
 * forged request would spend the token and sign the admin out. A cross-site form
 * cannot set a custom header, and a `fetch` that does is preflighted. The API
 * requires this exact name; see `admin-refresh-cookie.ts` there.
 */
const CSRF_HEADER = 'x-kadir-admin';

export type AdminApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      /** The outcome in the console's vocabulary — what a screen branches on and renders. */
      error: AdminAuthErrorCode;
      /** From the envelope's `details.retryAfterSeconds`, on a 429. */
      retryAfterSeconds?: number;
      /**
       * The HTTP status, when there was a response at all. What lets a detail screen
       * tell "no such record" (404) from "the request failed": the API's codes for those
       * have no console name, so `error` reads `session_invalid` for both.
       */
      status?: number;
      /** The envelope's `error.code`, as the API sent it, when it sent one. */
      code?: string;
      /** The envelope's `error.message`: developer-facing English, for logs, never for the screen. */
      message?: string;
      /** The envelope's `error.details`: field errors, retry hints, a `reason`. */
      details?: ApiErrorBody['details'];
    };

/** The codes that already name a console outcome pass through as themselves. */
const CONSOLE_CODES: ReadonlySet<string> = new Set(Object.values(ADMIN_AUTH_ERROR));

/**
 * The envelope's `error`, read field by field.
 *
 * Typed from the contract and still checked at runtime: a failure can come from a
 * proxy or a crashed upstream rather than the API, and a body that is not the
 * envelope reads as no code at all rather than as a thrown `TypeError`.
 */
function readEnvelope(body: unknown): Partial<ApiErrorBody> {
  const error = (body as Partial<ApiErrorEnvelope> | null)?.error as unknown;
  if (typeof error !== 'object' || error === null) return {};
  const { code, message, details } = error as Record<string, unknown>;
  return {
    ...(typeof code === 'string' ? { code: code as ApiErrorBody['code'] } : {}),
    ...(typeof message === 'string' ? { message } : {}),
    ...(typeof details === 'object' && details !== null && !Array.isArray(details)
      ? { details: details as ApiErrorBody['details'] }
      : {}),
  };
}

/**
 * The console's name for an envelope code.
 *
 * `rate_limited` is a throttle or a lockout and keeps its own message. Anything else
 * the console has no sentence for reads `session_invalid` with its `status`, which is
 * what `authenticated` renews on (a 401 only) and what a detail screen reads a 404
 * from. A 401 on a sign-in route means something narrower, and the call in
 * `endpoints/auth.ts` says what.
 */
function consoleErrorOf(code: string | undefined): AdminAuthErrorCode {
  if (code !== undefined && CONSOLE_CODES.has(code)) return code as AdminAuthErrorCode;
  return ADMIN_AUTH_ERROR.SESSION_INVALID;
}

function retryAfterOf(details: ApiErrorBody['details']): number | undefined {
  const value = details?.retryAfterSeconds;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skips the token and the retry. Only the two calls that run before a session exists. */
  anonymous?: boolean;
  /**
   * How to read a successful body. `json` for every call but a file download, which
   * reads the bytes as a `Blob` so a CSV's BOM and Arabic labels arrive untouched.
   */
  responseType?: 'json' | 'blob';
}

export async function send<T>(
  path: string,
  options: RequestOptions = {},
): Promise<AdminApiResult<T>> {
  const { body, anonymous, headers, responseType = 'json', ...rest } = options;
  const token = anonymous ? null : adminSession.token;

  try {
    const response = await fetch(`${API_ORIGIN}${path}`, {
      ...rest,
      // The refresh cookie rides on this. Without it the browser sends nothing
      // cross-origin and every reload would be a fresh login.
      credentials: 'include',
      cache: 'no-store',
      headers: {
        accept: responseType === 'blob' ? '*/*' : 'application/json',
        [CSRF_HEADER]: '1',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (response.ok) {
      const data: unknown =
        response.status === 204
          ? undefined
          : responseType === 'blob'
            ? await response.blob()
            : await response.json();
      return { ok: true, data: data as T };
    }

    const { code, message, details } = readEnvelope(await response.json().catch(() => null));
    const retryAfterSeconds = retryAfterOf(details);
    return {
      ok: false,
      status: response.status,
      error: consoleErrorOf(code),
      ...(code !== undefined ? { code } : {}),
      ...(message !== undefined ? { message } : {}),
      ...(details !== undefined ? { details } : {}),
      ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
    };
  } catch {
    // A network failure, a CORS refusal, a certificate the browser will not
    // accept. None of them is "your session ended", and saying so would send an
    // admin to re-authenticate over a dropped Wi-Fi connection.
    return { ok: false, error: ADMIN_AUTH_ERROR.UNAVAILABLE };
  }
}
